import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  BreadcrumbGroup,
  Button,
  Container,
  FormField,
  Header,
  Select,
  SpaceBetween,
  Spinner,
  Textarea,
} from "@cloudscape-design/components";
import { apiFetch } from "./apiFetch";
import { useDocumentTitle } from "./useDocumentTitle";
import { getProductSchema, formValuesFromPayload, PRODUCT_SCHEMAS } from "./productSchemas";
import CreateProductPage from "./CreateProductPage";

/**
 * Inverse of the server's buildContextProjectName (see services/stack-naming.ts): the context
 * JSON's top-level projectName has a `-${envName.toLowerCase()}` suffix appended for non-Prod
 * environments (no suffix for Prod), so recovering the real project name from a pasted context
 * JSON (see handleBootstrap below) needs to strip that same suffix back off using the env this
 * payload's own stackDef.taggingEnv resolves to (already available in `values` by the time this
 * runs). No-op for Prod or when the value doesn't end with the expected suffix (e.g. older
 * payloads predating this convention), so it degrades to passing the value through unchanged.
 */
function stripContextProjectNameSuffix(contextProjectName: string, envName: string): string {
  const isProd = (envName || "").trim().toLowerCase() === "prod";
  if (!contextProjectName || !envName || isProd) return contextProjectName;
  const suffix = `-${envName.trim().toLowerCase()}`;
  return contextProjectName.endsWith(suffix) ? contextProjectName.slice(0, -suffix.length) : contextProjectName;
}

interface StackInfo {
  documentId: string;
  name: string;
  project?: { documentId: string; name: string } | null;
  tags?: { key: string; value: string }[];
}

const PRODUCT_TYPE_OPTIONS = PRODUCT_SCHEMAS.map((s) => ({ label: s.title, value: s.key }));

/**
 * Loads the data needed to edit/redeploy an existing stack, then renders the same
 * schema-driven form CreateProductPage uses in "create" mode, prefilled from the most recent
 * deployment-request submitted for this stack.
 *
 * productType isn't stored on the stack itself, so it's resolved via
 * GET /deployment-requests/latest-by-stack/:stackName, which also returns the CDK payload used
 * for the resolved fields (formValuesFromPayload converts that payload back into form values).
 *
 * Stacks deployed outside this app (imported by tag rather than submitted through Create
 * Product) have no such deployment-request, so latest-by-stack 404s. For those, instead of a
 * dead end, the user can paste the original CDK context JSON (the same shape Jenkins receives
 * and the shape stored as a deployment-request's `payload`) to bootstrap the edit form once.
 * Submitting from that prefilled form creates a normal deployment-request, so every subsequent
 * edit of that stack goes through the regular latest-by-stack path automatically.
 */
export default function EditProductPage() {
  const { projectId, stackId } = useParams<{ projectId: string; stackId: string }>();
  const navigate = useNavigate();

  const [stack, setStack] = useState<StackInfo | null>(null);
  useDocumentTitle(`Edit ${stack?.name ?? "Product"}`);
  const [productType, setProductType] = useState<string | null>(null);
  const [initialValues, setInitialValues] = useState<Record<string, any> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [needsBootstrap, setNeedsBootstrap] = useState(false);

  // Bootstrap form state (only used when needsBootstrap is true)
  const [bootstrapProductType, setBootstrapProductType] = useState<{ label: string; value: string } | null>(null);
  const [bootstrapJson, setBootstrapJson] = useState("");
  const [bootstrapError, setBootstrapError] = useState("");
  const [bootstrapping, setBootstrapping] = useState(false);

  useEffect(() => {
    if (!stackId) { setLoading(false); return; }
    (async () => {
      try {
        const stackRes = await apiFetch(
          `/stacks/${stackId}?populate[project][fields][0]=name&populate[project][fields][1]=documentId&populate[tags][fields][0]=key&populate[tags][fields][1]=value`
        );
        if (!stackRes.ok) throw new Error(`Failed to load stack: ${stackRes.status}`);
        const stackJson = await stackRes.json();
        const stackData: StackInfo = stackJson.data;
        setStack(stackData);

        const latestRes = await apiFetch(`/deployment-requests/latest-by-stack/${encodeURIComponent(stackData.name)}`);
        if (!latestRes.ok) {
          if (latestRes.status === 404) {
            setNeedsBootstrap(true);
            return;
          }
          throw new Error(`Failed to load deployment history: ${latestRes.status}`);
        }
        const latestJson = await latestRes.json();
        const entry = latestJson.data ?? latestJson;

        const schema = getProductSchema(entry.productType);
        if (!schema) throw new Error(`Unknown product type "${entry.productType}" for this stack.`);

        const values = formValuesFromPayload(schema, entry.payload);
        // The stored CDK payload only carries the raw AWS account ID (accountID) and drops the
        // Strapi account relation's documentId/projectName (both are stripped out before being
        // persisted as payload, see the submit() controller), so pull the authoritative values
        // from the deployment-request entry itself instead of trying to recover them from the payload.
        values.projectName = entry.projectName ?? values.projectName;
        values.accountId = entry.account?.documentId ?? values.accountId;
        // If this stack has actual imported AWS tags (e.g. it was synced/discovered from AWS
        // rather than only ever deployed through this app), the live ICLProject tag is the most
        // authoritative source of the project name and takes priority over the deployment-request's
        // own projectName field, in case they've ever drifted apart.
        const importedIclProject = stackData.tags?.find((t) => t.key === "ICLProject")?.value;
        if (importedIclProject) values.projectName = importedIclProject;

        setProductType(entry.productType);
        setInitialValues(values);
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [stackId]);

  const handleBootstrap = async () => {
    setBootstrapError("");
    if (!bootstrapProductType) {
      setBootstrapError("Select the product type this stack was deployed as.");
      return;
    }
    const schema = getProductSchema(bootstrapProductType.value);
    if (!schema) {
      setBootstrapError(`Unknown product type "${bootstrapProductType.value}".`);
      return;
    }
    let payload: any;
    try {
      payload = JSON.parse(bootstrapJson);
    } catch {
      setBootstrapError("That's not valid JSON. Paste the exact context JSON used to deploy this stack.");
      return;
    }
    if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
      setBootstrapError("The pasted JSON must be an object, not an array or primitive value.");
      return;
    }

    setBootstrapping(true);
    try {
      const values = formValuesFromPayload(schema, payload);
      // Same as the normal path: prefer explicit identity fields from the pasted payload itself
      // (projectName is a top-level key in the context JSON) over whatever formValuesFromPayload
      // resolved. The context JSON's projectName carries a `-${env}` suffix for non-Prod
      // environments (see services/stack-naming.ts's buildContextProjectName on the server), so
      // strip it back off using the env formValuesFromPayload already resolved from
      // stackDef.taggingEnv, to recover the real project name rather than a suffixed one.
      if (typeof payload.projectName === "string") {
        values.projectName = stripContextProjectNameSuffix(payload.projectName, values.taggingEnv as string);
      }

      // The pasted JSON only carries the raw AWS account number (accountID), not our Strapi
      // account record's documentId, so look up the matching account by that number and fill it
      // in automatically. If no match is found (e.g. the account isn't registered in this app),
      // leave accountId blank for the user to pick manually instead of blocking the bootstrap.
      if (typeof payload.accountID === "string" || typeof payload.accountID === "number") {
        try {
          const accountsRes = await apiFetch(`/accounts`);
          if (accountsRes.ok) {
            const { data } = await accountsRes.json();
            const match = (data as { documentId: string; accountid: string }[]).find(
              (a) => a.accountid === String(payload.accountID)
            );
            if (match) values.accountId = match.documentId;
          }
        } catch {
          // Non-fatal: fall back to leaving accountId blank/editable.
        }
      }

      setProductType(bootstrapProductType.value);
      setInitialValues(values);
      setNeedsBootstrap(false);
    } finally {
      setBootstrapping(false);
    }
  };

  if (loading) return <Spinner size="large" />;

  if (error) {
    return (
      <SpaceBetween size="l">
        <BreadcrumbGroup
          items={[
            { text: "Projects", href: "/projects" },
            ...(projectId ? [{ text: "Project", href: `/projects/${projectId}` }] : []),
            { text: "Edit / Redeploy", href: "#" },
          ]}
          onFollow={(e) => { e.preventDefault(); navigate(e.detail.href); }}
        />
        <Alert type="error">{error}</Alert>
      </SpaceBetween>
    );
  }

  if (needsBootstrap) {
    return (
      <SpaceBetween size="l">
        <BreadcrumbGroup
          items={[
            { text: "Projects", href: "/projects" },
            ...(projectId ? [{ text: "Project", href: `/projects/${projectId}` }] : []),
            { text: "Edit / Redeploy", href: "#" },
          ]}
          onFollow={(e) => { e.preventDefault(); navigate(e.detail.href); }}
        />
        <Alert type="info">
          This stack has no deployment history in this app (it was likely deployed or imported outside of it), so its
          configuration can't be prefilled automatically. Paste the original CDK context JSON below to load it into
          the edit form once — after your next redeploy, this stack will have normal deployment history and future
          edits will prefill automatically.
        </Alert>
        <Container header={<Header variant="h2">Bootstrap from context JSON</Header>}>
          <SpaceBetween size="m">
            {bootstrapError && <Alert type="error">{bootstrapError}</Alert>}
            <FormField label="Product Type" description="What kind of product this stack was deployed as.">
              <Select
                selectedOption={bootstrapProductType}
                onChange={({ detail }) => setBootstrapProductType(detail.selectedOption as { label: string; value: string })}
                options={PRODUCT_TYPE_OPTIONS}
                placeholder="Select product type"
              />
            </FormField>
            <FormField
              label="Context JSON"
              description="The CDK context JSON originally used to deploy this stack (the same payload shape sent to Jenkins)."
            >
              <Textarea
                value={bootstrapJson}
                onChange={({ detail }) => setBootstrapJson(detail.value)}
                rows={14}
                placeholder='{"type": "lambda-container", "projectName": "myproj", "accountID": "111111111111", "tags": {...}, "stackDef": {...}}'
              />
            </FormField>
            <Button variant="primary" onClick={handleBootstrap} loading={bootstrapping}>Load configuration</Button>
          </SpaceBetween>
        </Container>
      </SpaceBetween>
    );
  }

  if (!stack || !productType || !initialValues) {
    return <Box color="text-body-secondary">Stack not found</Box>;
  }

  const backToProject = () => navigate(projectId ? `/projects/${projectId}` : "/projects");

  return (
    <CreateProductPage
      mode="edit"
      productTypeOverride={productType}
      initialValues={initialValues}
      projectIdOverride={projectId}
      onSubmitSuccess={backToProject}
      onCancelEdit={backToProject}
    />
  );
}
